import { countStringLength, trimString } from '../../scripts/utils';

const defaultSelectors = {
  input: 'input',
};

class TextField {
  constructor(elementOrSelector) {
    this.selectors = defaultSelectors;
    this.elements = this.getElements(elementOrSelector);

    this.value = this.elements.input.value;

    this.cursorPosition = this.getCursorPosition();
    this.previousCursorPosition = null;

    const maxLength = this.elements.container.getAttribute('data-max-length');

    this.maxLength = maxLength ? Number(maxLength) : null;

    this.eventSubscriptions = {
      input: [],
    };

    this.setupEventListeners();
  }

  getValue() {
    return this.value;
  }

  setValue(value) {
    this.value = value;
  }

  getPreviousValue() {
    const value = this.getValue();
    const cursorPosition = this.getCursorPosition();
    const previousCursorPosition = this.getPreviousCursorPosition();

    let previousValue = '';

    if (previousCursorPosition === 0) {
      // The new value has been added at the beginning of the input. Thus the previous value must be
      // everything after the current cursor position.
      previousValue = value.slice(cursorPosition);
    } else if (cursorPosition === value.length) {
      // The new value has been added to the end of the input. Thus the previous value must be
      // everything before the previous cursor position.
      previousValue = value.slice(0, previousCursorPosition);
    } else {
      // The new value has been added somewhere between the beginning and the end of the input.
      // Thus the value must be around the previous and current cursor position.
      previousValue = value.slice(0, previousCursorPosition);
      previousValue += value.slice(cursorPosition);
    }

    return previousValue;
  }

  getCursorPosition() {
    return this.elements.input.selectionStart;
  }

  setCursorPosition(cursorPosition) {
    this.cursorPosition = cursorPosition;
  }

  getPreviousCursorPosition() {
    return this.previousCursorPosition;
  }

  setPreviousCursorPosition(previousCursorPosition) {
    this.previousCursorPosition = previousCursorPosition;
  }

  getInputLength() {
    return countStringLength(this.value);
  }

  getElements(elementOrSelector) {
    let container = elementOrSelector;

    if (typeof elementOrSelector === 'string') {
      container = document.querySelector(elementOrSelector);
    }

    return {
      container,
      input: container.querySelector(this.selectors.input),
    };
  }

  handleBeforeInput({ target }) {
    this.setPreviousCursorPosition(target.selectionStart);
  }

  handleInput({ target }) {
    this.setValue(target.value);
    this.setCursorPosition(target.selectionStart);

    // If the character limit is exceeded we need to trim and update the
    // input elements value to match the max length again.
    if (this.getInputLength() > this.maxLength) {
      const cursorPosition = this.getCursorPosition();
      let previousCursorPosition = this.getPreviousCursorPosition();

      const cursorPositionDelta = cursorPosition - previousCursorPosition;

      // If the cursor position delta is greater than 1 that means that a value
      // with a length greater than 1 has been pasted from the users clipboard.
      if (cursorPositionDelta > 1) {
        const value = this.getValue();
        const previousValue = this.getPreviousValue();
        const addedValue = value.slice(previousCursorPosition, cursorPosition);

        const matches = [];

        // Find the string with the longest length that does not exceed the normalized
        // character limit. For instance, if the current value is "test" and the field has
        // a max length of 10 we can still input a value that has 6 characters.
        // Both "123456" (length: 6) and "12345👨‍👩‍👧‍👦" (length: 16) would be a match here.
        for (let i = 0; i < addedValue.length; i += 1) {
          // Loop over each character of the addedValue string and build a new string consisting of
          // the string until the current character plus the character itself. For instance, if the
          // addedValue is "test" the newly build string would be "t" in the first iteration,
          // "te" in the second iteration, and so on.
          const char = addedValue[i];
          const stringUntilChar = addedValue.slice(0, i) + char;

          if (
            // This rule is not compatible with prettier.
            // eslint-disable-next-line operator-linebreak
            countStringLength(stringUntilChar) <=
            this.maxLength - countStringLength(previousValue)
          ) {
            matches.push(stringUntilChar);
          }
        }

        // There are no matches if the previous value has already reached the max length.
        if (matches.length > 0) {
          // Updating the previous cursor position "simulates" the scenario that only a single
          // character has been added to the input value.
          previousCursorPosition += matches.at(-1).length;
        }
      }

      const newValue = trimString(
        this.getValue(),
        previousCursorPosition,
        cursorPosition,
      );

      // This is fine here since it's an HTMLElement.
      // eslint-disable-next-line no-param-reassign
      target.value = newValue;

      this.setValue(target.value);

      // Prevent the cursor from jumping to the end of the line due to the
      // update of the input value above.
      target.setSelectionRange(previousCursorPosition, previousCursorPosition);

      this.setCursorPosition(previousCursorPosition);
    }

    this.eventSubscriptions.input.forEach((callback) => {
      callback(target);
    });
  }

  setupEventListeners() {
    this.elements.input.addEventListener('beforeinput', (event) => {
      this.handleBeforeInput(event);
    });

    this.elements.input.addEventListener('input', (event) => {
      this.handleInput(event);
    });
  }

  on(type, callback) {
    if (!this.eventSubscriptions[type]) {
      throw new Error(`Invalid text field event type: ${type}`);
    }

    this.eventSubscriptions[type].push(callback);
  }
}

export default TextField;
